/**
 * Контракт сводных показателей дашборда — Блок 2 · Ф7 · В13′.
 *
 * ─── ЧТО ЭТО ЗА ОТВЕТ ─────────────────────────────────────────────────────
 *
 * `GET /summary` отдаёт ОДНИМ телом три элемента нового ряда дашборда
 * (**Р-134, «Сводные показатели — новым рядом на дашборде, без экрана
 * „Сводка"»**): плитку «Разделы по бюро», плитку «Просрочено» и ленту «Сроки».
 * Экрана и маршрута у сводки нет — ряд живёт на существующем `/dashboard`.
 *
 * ─── ПОЧЕМУ СВОЙ ФАЙЛ, А НЕ ДОБАВКА В `dashboard.ts` ──────────────────────
 *
 * `dashboard.ts` — контракт СТАРЫХ плиток, и когорта у них другая: они считают
 * **по отделу** (`projectsInDepartmentWhere`), новый ряд — **по бюро**
 * (развилка 1 плана Ф7). Слитые в один файл, две когорты рано или поздно
 * прочитались бы как одна. Отдельный модуль держит границу видимой; ровно по
 * этой же причине у сводки свой серверный каталог `apps/api/src/summary/`, а не
 * метод в `DashboardController`.
 *
 * ═══ ⚠️ ГОТОВЫХ ПРИЗНАКОВ СРОЧНОСТИ В ОТВЕТЕ НЕТ — ЭТО НЕСУЩЕЕ ════════════
 *
 * Строка ленты несёт `dueDate` и `status`, а `today` приходит в корне ответа;
 * «просрочено» и «через N дней» клиент считает функциями `@aris/shared`
 * (`isOverdue`, `daysUntilIso`). Положи сервер сюда булево `overdue` — и в
 * системе стало бы ТРИ источника признака срочности (карточка, лента, сервер)
 * при одном авторитете `isUrgent`/`isOverdue`. Это долг #85 дословно, и
 * заводить его третий экземпляр волна отказывается.
 *
 * ⚠️ `today` — СЕРВЕРНОЕ, из Postgres в `Europe/Moscow` (`queryTodayMsk`). Без
 * него клиент считал бы признаки от `new Date()` браузера и с 00:00 до 03:00
 * MSK расходился бы с сервером на сутки.
 */
import { z } from 'zod';
import { WorkStatus } from './work';

const UuidSchema = z.string().uuid();

/** `YYYY-MM-DD` — та же синтаксическая проверка, что в `work.ts`. */
const IsoDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Дата должна быть в формате ГГГГ-ММ-ДД');

const CountSchema = z.number().int().nonnegative();

/**
 * Одна доля распределения разделов по состояниям.
 *
 * ⚠️ СПИСОК УПОРЯДОЧЕН СЕРВЕРОМ (`tallySectionStatuses`) и содержит ВСЕ четыре
 * состояния, включая нулевые. Клиент рисует его подряд и своего порядка шкалы
 * не заводит — см. разбор у самой функции.
 */
export const SummarySectionTallySchema = z
  .object({
    status: z.nativeEnum(WorkStatus),
    count: CountSchema,
  })
  .strict();
export type SummarySectionTally = z.infer<typeof SummarySectionTallySchema>;

/**
 * Строка ленты «Сроки».
 *
 * ⚠️ `sectionName` — уже ВЫБРАННОЕ имя (`sectionDisplayName` = имя пункта
 * каталога, иначе колонка `Section.name`). Клиенту выбирать нечего: правило
 * «имя берётся из каталога, колонка — запасной след» живёт на сервере в
 * единственном экземпляре (`section/section-display-name.ts`).
 *
 * ⚠️ `sectionGroupId` и `sectionHasCatalogItem` — НЕ показ, а АДРЕС. По ним
 * клиент собирает ссылку, раскрывающую нужный узел дерева разделов в карточке
 * объекта: у раздела в группе родитель `g:<groupId>`, у раздела без пункта
 * каталога — служебный узел «Вне справочника», у раздела из пункта верхнего
 * уровня родителя нет вовсе. Три случая различаются только этой парой полей.
 *
 * ═══ ИСПОЛНИТЕЛЬ В СТРОКЕ ЕСТЬ — **Р-141** ═══════════════════════════════
 *
 * 🚫 Прежняя редакция говорила ~~«ИСПОЛНИТЕЛЯ В СТРОКЕ НЕТ … дашборд сегодня
 * фамилий не показывает — новый канал персональных данных»~~. **Оба довода
 * были неверны, и это проверено, а не переспорено:**
 *
 *   - дашборд фамилии показывает УЖЕ — `EmployeeLoadTable` печатает
 *     `row.fullName` (`apps/web/src/pages/dashboard/components/employee-load-table.tsx`)
 *     на том же экране, под теми же MANAGER/ADMIN. Нового канала ПДн строка
 *     ленты не открывает;
 *   - решение круга Ф7 фамилию всё-таки завело — **Р-141, «А — с фамилией,
 *     как в макете»**, и макет, по которому владелец принимал Р-134, её
 *     показывал (`.lane-person`).
 *
 * ⚠️ `assignee` ОБЪЯВЛЕН NULLABLE ПО БУКВЕ Р-141 («работа без исполнителя —
 * прочерк»), но сегодня `null` НЕДОСТИЖИМ: `Work.assigneeId` — `NOT NULL`
 * (миграция 046, `schema.prisma`, «ОДИН исполнитель на работу»). То есть ветка
 * прочерка на клиенте — запас под формулировку решения, а не живой случай, и
 * называть её «покрытой» нельзя: проверить её на стенде нечем.
 */
export const SummaryDeadlineRowSchema = z
  .object({
    workId: UuidSchema,
    /** Пустое название — легальное состояние: клиент показывает имя раздела. */
    title: z.string().nullable(),
    dueDate: IsoDateSchema,
    status: z.nativeEnum(WorkStatus),
    projectId: UuidSchema,
    projectName: z.string(),
    sectionId: UuidSchema,
    sectionName: z.string(),
    sectionGroupId: UuidSchema.nullable(),
    sectionHasCatalogItem: z.boolean(),
    /** Исполнитель работы (Р-141). `null` — см. врезку выше: недостижим. */
    assignee: z.object({ id: UuidSchema, fullName: z.string() }).strict().nullable(),
  })
  .strict();
export type SummaryDeadlineRow = z.infer<typeof SummaryDeadlineRowSchema>;

/**
 * Ширина окна ленты «Сроки» в сутках — решение ПЛАНА Ф7 (развилка 3), не
 * владельца: Р-134 требует «приближающиеся и просроченные» и числа не задаёт.
 * Семь суток — горизонт недели, в котором руководитель перераспределяет людей.
 *
 * Живёт в общем пакете, потому что окно читают обе стороны: сервер отбирает по
 * нему строки, а веб называет его в подписи ленты. Разойдись эти два числа —
 * подпись обещала бы не то, что показано.
 *
 * ⚠️ ЭТО НЕ ПОРОГ «СРОЧНО». Признак «Срочно» — `isUrgent` (одни сутки), и он
 * остаётся единственным авторитетом срочности (долг #85). Лента — потребитель
 * окна, а не второе правило.
 */
export const SUMMARY_DEADLINE_WINDOW_DAYS = 7;

/**
 * Потолок ПРОСРОЧЕННЫХ строк ленты «Сроки» — решение оркестратора (фикс-пак 2
 * В13′), не владельца.
 *
 * ═══ ЗАЧЕМ ПОТОЛОК ВООБЩЕ ════════════════════════════════════════════════
 *
 * Без него лента печатала бы КАЖДУЮ просроченную и приближающуюся работу
 * бюро: на стенде это десятки строк (33 из 62 работ — следы проверок), на
 * проде — сотни. Дашборд удлинялся бы на экраны, отодвигая таблицы загрузки,
 * а размер ответа ничем не ограничивался. Макет показывал пять строк.
 *
 * ═══ ⚠️ ПОТОЛОК ОДИН НА ДВЕ ГРУППЫ — ЭТО ДЕФЕКТ, И ОН БЫЛ ЗДЕСЬ ══════════
 *
 * 🚫 Прежняя редакция ставила ОДИН потолок `20` на общий, отсортированный по
 * возрастанию `dueDate` список и объявляла свойством, что «просроченные не
 * теряются первыми». Зеркальное свойство она не называла, а оно и есть
 * отказ: **приближающиеся теряются первыми, и целиком.** Просроченный срок
 * лексикографически меньше сегодняшнего, значит при 20 и более просроченных
 * в срез не попадала НИ ОДНА строка из окна `[today; today + 7]` — на ленте
 * «просроченные и приближающиеся» (Р-134) приближающихся не оставалось вовсе.
 * На стенде это не редкий случай, а обычный: там 33 просроченные работы из 62.
 *
 * **Теперь потолок применяется ПО ГРУППАМ**, и группы считаются порознь:
 *   1. просроченные — не более `SUMMARY_OVERDUE_LANE_LIMIT`, **ближайшие к
 *      сегодня ПЕРВЫМИ** (наименьшая просрочка сверху). Порядок именно такой,
 *      а не «самая давняя первой»: под потолком видимой обязана остаться
 *      свежая просрочка, которую ещё можно догнать, а не тридцать залежалых
 *      проверочных строк, вытеснивших всё остальное. Это же порядок макета
 *      (`.lane`: «до 11.09» перед «до 09.09»);
 *   2. приближающиеся — не более `SUMMARY_UPCOMING_LANE_LIMIT`, ближайшая
 *      первой.
 * Группа, не выбравшая свой потолок, чужой не добирает: иначе пустая неделя
 * молча возвращала бы ленту к прежнему поведению.
 *
 * ⚠️ ПОТОЛОК РЕЖЕТ ХВОСТ, А НЕ ВЫБОРКУ, и итоги (`overdueTotal`,
 * `upcomingTotal`) считаются ДО среза — поэтому «и ещё N» называет настоящий
 * остаток, а не остаток страницы. Хвост на экране ОДИН и складывает остатки
 * обеих групп: читателю нужно число «сколько не показано», а не разбор, из
 * какой половины ленты оно набралось.
 *
 * 12 + 8 = те же двадцать строк, что и прежде: высота ряда не менялась,
 * менялось распределение. Постраничного листания у ленты нет намеренно — ряд
 * дашборда не рабочая поверхность, а указатель.
 *
 * ═══ ⚠️ ОБЪЯВЛЕНЫ ВЫШЕ СХЕМЫ НАМЕРЕННО, И ЭТО НЕ ВКУСОВЩИНА ══════════════
 *
 * Оба потолка — часть ДОГОВОРА, а не только серверного среза:
 * `DashboardSummarySchema` вешает их через `.max()` (приёмка Codex) в момент
 * вычисления модуля. До фикс-пака 4 объявления лежали НИЖЕ схемы — тогда
 * читателю было всё равно, теперь нет.
 *
 * ⚠️ Отказ здесь НЕ «падение при импорте», и проверено это порчей, а не
 * рассуждением. Верни объявления вниз — `tsc` валит сборку пакета
 * (`TS2448 used before its declaration`, `TS2454 used before being assigned`),
 * и это единственный сторож. Если же собранный файл всё-таки получен (tsc
 * печатает ошибки, но по умолчанию ВСЁ РАВНО эмитит, а в `dist` ссылка
 * компилируется в `exports.SUMMARY_…` — обычное чтение свойства, не TDZ), то
 * на выходе `.max(undefined)`, и zod такой потолок молча принимает: лента
 * любой длины проходит разбор. То есть цена перестановки — не шумное падение,
 * а ТИХО испарившийся потолок. Переносить их обратно вниз нельзя.
 */
export const SUMMARY_OVERDUE_LANE_LIMIT = 12;

/**
 * Потолок ПРИБЛИЖАЮЩИХСЯ строк ленты — вторая половина правила, разобранного
 * у `SUMMARY_OVERDUE_LANE_LIMIT`. Меньше просроченного потому, что окно здесь
 * всего семь суток, а просрочка копится без границы.
 */
export const SUMMARY_UPCOMING_LANE_LIMIT = 8;

/**
 * Тело `GET /summary`.
 *
 * ⚠️ СХЕМА СТРОГАЯ — ПО ОБРАЗЦУ СОСЕДНЕГО ДОГОВОРА, а не вопреки ему.
 *
 * 🚫 Прежняя редакция писала: ~~«`.strict()` здесь нет … образец —
 * `apps/web/src/api/dashboard.ts`»~~. **Образец был прочитан наоборот:**
 * `packages/shared/src/dashboard.ts` кладёт `.strict()` на КАЖДЫЙ объект
 * ответа и объявляет это правилом в собственной шапке («Strict objects. Every
 * shape uses `.strict()` so unknown keys throw»). То есть нестрогая схема была
 * отступлением от договора, поданным как следование ему.
 *
 * Довод «аддитивное серверное поле уронит вкладку» относится к старым плиткам
 * ровно так же и решён там тем же способом — строгой схемой: расхождение формы
 * обязано стать ВИДИМОЙ ошибкой запроса, а не тихо принятым мусором.
 *
 * ⚠️ 🚫 ЦЕНА НАЗВАНА ЧЕСТНО, И ПРЕЖНЯЯ РЕДАКЦИЯ ЕЁ ПРЯТАЛА. Она писала:
 * ~~«Сводка обновляется раз в 60 с, так что вкладка догоняет выкат сама»~~ —
 * **это неверно**. Обновление раз в 60 с прогоняет новый ответ через ТУ ЖЕ
 * схему ТОГО ЖЕ старого бандла; от повторного запроса бандл не меняется.
 * Значит открытая вкладка, пережившая выкат с аддитивным полем, получает
 * «Не удалось загрузить сводку», и кнопка «Повторить» повторяет тот же разбор
 * — до ручной перезагрузки страницы (`ZodError` возникает внутри `queryFn`,
 * то есть это ошибка ЗАПРОСА, и `route-error-boundary` с его
 * `window.location.reload()` до неё не достаёт). Окно узкое — только вкладка,
 * открытая через выкат, — но оно есть, и молчать о нём нельзя.
 */
export const DashboardSummarySchema = z
  .object({
    /** Серверное «сегодня» (`Europe/Moscow`) — из него клиент считает признаки. */
    today: IsoDateSchema,
    /** Живые разделы живых объектов когорты. */
    sections: z
      .object({
        total: CountSchema,
        /** Все четыре состояния, в порядке шкалы, нули включительно. */
        byStatus: z.array(SummarySectionTallySchema),
      })
      .strict(),
    /** Живых объектов бюро в когорте — в том числе без единой работы. */
    projectCount: CountSchema,
    /**
     * ВСЕ живые проектные работы когорты — закрытые включительно.
     *
     * ⚠️ ЭТО НЕ ЗНАМЕНАТЕЛЬ ПРОСРОЧКИ, И РАЗНИЦА НЕСУЩАЯ. Поле кормит подпись
     * плитки «Разделы по бюро» («K объектов · M работ»), где по макету стоит
     * ОБЩЕЕ число работ бюро. Знаменатель просрочки — `overdue.total`, и он
     * ýже (только незакрытые, **Р-142**). Прежде подпись плитки разделов брала
     * число из `overdue.total`, то есть из знаменателя ЧУЖОЙ плитки: сузь его —
     * и подпись соседа поехала бы молча. Теперь у каждой плитки своё поле.
     */
    worksTotal: CountSchema,
    /**
     * Доля просроченного — ПО РАБОТАМ (**Р-135**), знаменатель — ТОЛЬКО
     * НЕЗАКРЫТЫЕ (**Р-142, «Б — только из незакрытых»**).
     *
     * `works` — живые проектные работы со сроком в прошлом и незакрытым
     * состоянием (`isOverdue`); `total` — живые проектные работы с незакрытым
     * состоянием (`isOpenStatus`), безотносительно срока. Оба множества
     * считаются функциями `@aris/shared`, а не условием `where`.
     *
     * ⚠️ ПРЕЖДЕ `total` СЧИТАЛ И ЗАКРЫТЫЕ — расхождение с буквой Р-135 («÷ все
     * живые незакрытые») и с приёмочным SQL плана (`status <> 'DONE'`), из-за
     * которого независимая сверка на приёмке дала бы другое число при
     * исправном коде. Разобрано адвокатом дьявола В13′ и закрыто **Р-142**.
     *
     * Процента в ответе нет: его считает показ, и делить на ноль ему же.
     */
    overdue: z
      .object({
        works: CountSchema,
        total: CountSchema,
      })
      .strict(),
    /**
     * Лента «Сроки» — ДВЕ ГРУППЫ, каждая со своим потолком и своим итогом.
     *
     * ⚠️ `overdue` ЗДЕСЬ — НЕ ТО ЖЕ, ЧТО `overdue` В КОРНЕ. В корне это плитка
     * (два числа), здесь — группа строк ленты. Разбор обеих — у
     * `SUMMARY_OVERDUE_LANE_LIMIT`.
     */
    deadlines: z
      .object({
        /**
         * Просроченные, БЛИЖАЙШИЕ К СЕГОДНЯ ПЕРВЫМИ (наименьшая просрочка
         * сверху), не более `SUMMARY_OVERDUE_LANE_LIMIT` штук.
         *
         * ⚠️ ПОТОЛОК ЗАКРЕПЛЁН ЗДЕСЬ, А НЕ ТОЛЬКО В СРЕЗЕ СЕРВЕРА (приёмка
         * Codex, фикс-пак 4). Прежде число жило в одном экземпляре — в
         * `summary.service.ts`; сними там `.slice()` — и договор молча принял
         * бы ленту любой длины, а отказом стала бы высота ряда на экране.
         * Теперь лишняя строка — ВИДИМАЯ ошибка разбора, ровно как лишний
         * ключ у `.strict()`.
         */
        overdue: z.array(SummaryDeadlineRowSchema).max(SUMMARY_OVERDUE_LANE_LIMIT),
        /** Сколько просроченных подошло ДО потолка группы. */
        overdueTotal: CountSchema,
        /**
         * Сроки в окне `[today; today + SUMMARY_DEADLINE_WINDOW_DAYS]`,
         * ближайший первым, не более `SUMMARY_UPCOMING_LANE_LIMIT` штук.
         *
         * Потолок закреплён `.max()` — разбор у соседней группы выше.
         */
        upcoming: z.array(SummaryDeadlineRowSchema).max(SUMMARY_UPCOMING_LANE_LIMIT),
        /** Сколько приближающихся подошло ДО потолка группы. */
        upcomingTotal: CountSchema,
      })
      .strict(),
  })
  .strict();
export type DashboardSummary = z.infer<typeof DashboardSummarySchema>;
